import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { fetchWithToken } from "../api/apiutils";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Divider,
  CardHeader,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Коды статусов по блокам анализа (см. постановку)
const ANALYSIS_STATUS = {
  PENDING: 1,
  ANALYZED: 2,
  CANT_ANALYZE: 3,
  HAVENT_ANALYZED: 4,
  IN_PROGRESS: 5,
  BANNED: 6,
  NOT_FOUND: 7,
  UNAVAILABLE: 8,
};

const STATUS_LABELS = {
  [ANALYSIS_STATUS.PENDING]: "В очереди",
  [ANALYSIS_STATUS.ANALYZED]: "Готово",
  [ANALYSIS_STATUS.CANT_ANALYZE]: "Не удалось проанализировать",
  [ANALYSIS_STATUS.HAVENT_ANALYZED]: "Данных нет",
  [ANALYSIS_STATUS.IN_PROGRESS]: "Считается",
  [ANALYSIS_STATUS.BANNED]: "Заблокировано",
  [ANALYSIS_STATUS.NOT_FOUND]: "Не найдено",
  [ANALYSIS_STATUS.UNAVAILABLE]: "Недоступно",
};

const POLL_INTERVAL_MS = 5000;

const BinActiveUserPage = () => {
  const { bin, analysisId } = useParams();

  // --- статус анализа (isReady/isProcessing/статусы блоков) ---
  const [analysisMeta, setAnalysisMeta] = useState(null);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [noData, setNoData] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const [data, setData] = useState(null);

  // --- заявки ---
  const [summaryData, setSummaryData] = useState(null);
  const [applicationsByYear, setApplicationsByYear] = useState({});
  // --- контракты ---
  const [contractsSummary, setContractsSummary] = useState(null);
  const [contractsByYear, setContractsByYear] = useState({});

  const [taxes, setTaxes] = useState({});

  const pollTimerRef = useRef(null);
  const loadedRef = useRef(false); // чтобы не грузить блоки повторно при каждом опросе

  const getValue = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value?.length === 0
    ) {
      return "Не указано";
    }
    return value;
  };

  function formatDate(dateString) {
    if (!dateString) return "";

    const date = new Date(dateString);

    return date.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // --- загрузка отдельных блоков карточки, каждый только если его статус Analyzed(2) ---
  const loadCompanyData = async (meta) => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    if (meta.detailInformationStatus === ANALYSIS_STATUS.ANALYZED) {
      try {
        const companies = await fetchWithToken(
          `/api/companies?AnalysisId=${analysisId}&Identifier=${bin}`,
        );
        setData(await companies.json());
      } catch (error) {
        console.error("Ошибка при загрузке карточки компании:", error);
      }
    }

    if (meta.applicationsSummaryStatus === ANALYSIS_STATUS.ANALYZED) {
      try {
        const summaryResponse = await fetchWithToken(
          `/api/companies/applications/summary?AnalysisId=${analysisId}&Identifier=${bin}`,
        );
        setSummaryData(await summaryResponse.json());
      } catch (error) {
        console.error("Ошибка при загрузке summary:", error);
      }
    }

    if (meta.contractsSummaryStatus === ANALYSIS_STATUS.ANALYZED) {
      try {
        const contractsSummaryResponse = await fetchWithToken(
          `/api/companies/contracts/summary?AnalysisId=${analysisId}&Identifier=${bin}`,
        );
        setContractsSummary(await contractsSummaryResponse.json());
      } catch (error) {
        console.error("Ошибка при загрузке contracts summary:", error);
      }
    }

    if (meta.taxesStatus === ANALYSIS_STATUS.ANALYZED) {
      try {
        const taxesResponse = await fetchWithToken(
          `/api/companies/taxes?AnalysisId=${analysisId}&Identifier=${bin}`,
        );
        setTaxes(await taxesResponse.json());
      } catch (error) {
        console.error("Ошибка при загрузке налогов:", error);
      }
    }
  };

  // --- опрос GET /api/companies/analyses по БИН, пока анализ не готов ---
  const checkAnalysis = async () => {
    try {
      const res = await fetchWithToken(
        `/api/companies/analyses?Identifier=${bin}&Page=1&PageLimit=1`,
      );
      const result = await res.json();
      const analysis = result?.data?.[0];

      if (!analysis) {
        setAnalysisMeta(null);
        setIsAnalysing(false);
        setNoData(true);
        stopPolling();
        return;
      }

      setAnalysisMeta(analysis);

      if (analysis.isReady) {
        setIsAnalysing(false);
        setNoData(false);
        stopPolling();
        loadCompanyData(analysis);
      } else if (analysis.isProcessing) {
        setIsAnalysing(true);
        setNoData(false);
        startPolling();
      } else {
        // всё отработало, но детальной информации по компании нет (~9% случаев)
        setIsAnalysing(false);
        setNoData(true);
        stopPolling();
      }
    } catch (error) {
      console.error("Ошибка при проверке статуса анализа:", error);
    }
  };

  const startPolling = () => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setInterval(checkAnalysis, POLL_INTERVAL_MS);
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const handleRestartAnalysis = async () => {
    setRestarting(true);
    try {
      const response = await fetchWithToken("/api/companies/analyses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: bin, type: "OneTime" }),
      });
      if (!response.ok) throw new Error(response.statusText);
      // POST мог вернуть тот же id (если ещё < 5 минут) либо новый —
      // в обоих случаях просто продолжаем опрос по БИН
      loadedRef.current = false;
      setNoData(false);
      await checkAnalysis();
    } catch (error) {
      console.error("Ошибка при перезапуске анализа:", error);
    } finally {
      setRestarting(false);
    }
  };

  // --- загрузка заявок (по клику на КТРУ/метод закупки) ---
  const loadApplications = async ({ year, truCode, tradeMethodId }) => {
    if (analysisMeta?.applicationsStatus !== ANALYSIS_STATUS.ANALYZED) return;

    let url = `/api/companies/applications?AnalysisId=${analysisId}&Identifier=${bin}`;
    if (year) url += `&Year=${year}`;
    if (truCode) url += `&TruCode=${truCode}`;
    if (tradeMethodId) url += `&TradeMethodId=${tradeMethodId}`;

    try {
      const applicationsResponse = await fetchWithToken(url);
      const applicationData = await applicationsResponse.json();

      setApplicationsByYear((prev) => ({
        ...prev,
        [year]: applicationData.data || [],
      }));
    } catch (error) {
      console.error("Ошибка при загрузке applications:", error);
    }
  };

  // --- загрузка контрактов (по клику на КТРУ/метод закупки) ---
  const loadContracts = async ({ year, truCode, tradeMethodId }) => {
    if (analysisMeta?.contractsStatus !== ANALYSIS_STATUS.ANALYZED) return;

    let url = `/api/companies/contracts?AnalysisId=${analysisId}&Identifier=${bin}`;
    if (year) url += `&Year=${year}`;
    if (truCode) url += `&TruCode=${truCode}`;
    if (tradeMethodId) url += `&TradeMethodId=${tradeMethodId}`;

    try {
      const contractsResponse = await fetchWithToken(url);
      const contractsData = await contractsResponse.json();

      setContractsByYear((prev) => ({
        ...prev,
        [year]: contractsData || [],
      }));
    } catch (error) {
      console.error("Ошибка при загрузке contracts:", error);
    }
  };

  useEffect(() => {
    // сброс состояния при переходе на другой БИН/анализ
    loadedRef.current = false;
    setAnalysisMeta(null);
    setData(null);
    setSummaryData(null);
    setContractsSummary(null);
    setApplicationsByYear({});
    setContractsByYear({});
    setTaxes({});
    setNoData(false);

    checkAnalysis();

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bin, analysisId]);

  return (
    <>
      {/* --- статус анализа --- */}
      {isAnalysing && (
        <Box sx={{ margin: "20px", fontSize: 20 }}>
          <Box
            sx={{
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={24} />
            Идёт анализ компании, подождите — страница обновится автоматически
          </Box>
        </Box>
      )}

      {noData && (
        <Box sx={{ margin: "20px", fontSize: 20 }}>
          <Box sx={{ marginBottom: "20px" }}>
            Детальная информация по компании не найдена
            {analysisMeta &&
              ` (статус: ${STATUS_LABELS[analysisMeta.detailInformationStatus] || "неизвестно"})`}
          </Box>
          <Button
            variant="contained"
            color="primary"
            disabled={restarting}
            onClick={handleRestartAnalysis}
          >
            {restarting ? (
              <CircularProgress size={20} />
            ) : (
              "Перезапустить анализ"
            )}
          </Button>
        </Box>
      )}

      {/* --- карточка компании --- */}
      {!isAnalysing && !noData && (
        <div className="companies">
          {data ? (
            <Card sx={{ margin: "20px" }}>
              {" "}
              <CardHeader title={getValue(data.name)} />{" "}
              <CardContent>
                {" "}
                <Divider sx={{ my: 1 }} />{" "}
                <Box sx={{ display: "grid", gap: 1 }}>
                  {" "}
                  <Typography>
                    {" "}
                    <strong>БИН/ИИН:</strong> {getValue(data.identifier)}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Дата регистрации:</strong>{" "}
                    {getValue(data.registrationDate)}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Наименование организации:</strong>{" "}
                    {getValue(data.name)}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Электронная почта:</strong>{" "}
                    {getValue(data.contact?.email)}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Веб-сайт:</strong>{" "}
                    {getValue(data.contact?.website)}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Телефоны:</strong>{" "}
                    {Array.isArray(data.contact?.phoneNumbers)
                      ? data.contact.phoneNumbers.join(", ")
                      : getValue(data.contact?.phoneNumbers)}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Тип организации:</strong> {getValue(data.type)}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>ОКЭД (Вид деятельности):</strong>{" "}
                    {Array.isArray(data.okeds) ? (
                      <ul>
                        {" "}
                        {data.okeds.map((o, idx) => (
                          <li key={idx}>
                            {" "}
                            {o.code} - {o.name}{" "}
                          </li>
                        ))}{" "}
                      </ul>
                    ) : (
                      getValue(data.okeds?.name)
                    )}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Адреса:</strong>{" "}
                    {Array.isArray(data.addresses)
                      ? data.addresses.join(", ")
                      : getValue(data.addresses)}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Поставщик:</strong>{" "}
                    {data.supplier ? "Да" : "Нет"}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Заказчик:</strong>{" "}
                    {data.customer ? "Да" : "Нет"}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Организатор:</strong>{" "}
                    {data.organizer ? "Да" : "Нет"}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Нац. компания:</strong>{" "}
                    {data.isNationalCompany ? "Да" : "Нет"}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Организация инвалидов:</strong>{" "}
                    {data.isAssocWithDisab ? "Да" : "Нет"}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Малое предприятие:</strong>{" "}
                    {data.isSmallEmployer ? "Да" : "Нет"}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Гос. монополия:</strong>{" "}
                    {data.isStateMonopoly ? "Да" : "Нет"}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Патронажный поставщик:</strong>{" "}
                    {data.isPatronymicSupplyer ? "Да" : "Нет"}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Резидент:</strong>{" "}
                    {data.isResident ? "Да" : "Нет"}{" "}
                  </Typography>{" "}
                  <Typography>
                    {" "}
                    <strong>Квазигос. сектор:</strong>{" "}
                    {data.qvazi ? "Да" : "Нет"}{" "}
                  </Typography>{" "}
                </Box>{" "}
              </CardContent>{" "}
            </Card>
          ) : (
            <Box sx={{ padding: "20px", color: "text.secondary" }}>
              Детальная информация по компании недоступна
              {analysisMeta &&
                ` (статус: ${STATUS_LABELS[analysisMeta.detailInformationStatus] || "неизвестно"})`}
            </Box>
          )}
        </div>
      )}

      {/* --- заявки --- */}
      {!isAnalysing && !noData && (
        <div className="summary" style={{ margin: "20px" }}>
          {summaryData ? (
            <Card sx={{ marginTop: "20px" }}>
              <CardHeader title="Заявки по годам" />
              <CardContent>
                <Typography sx={{ mb: 2 }}>
                  <strong>Всего заявок:</strong> {getValue(summaryData.all)}
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  <strong>Количество побед:</strong>{" "}
                  {getValue(summaryData.numberOfWinnings)}
                </Typography>

                {summaryData.byYear?.map((yearItem, idx) => (
                  <Accordion key={idx} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        {yearItem.year} — всего: {getValue(yearItem.all)},
                        побед: {getValue(yearItem.numberOfWinnings)}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: "grid", gap: 2 }}>
                        <Divider>
                          <strong>по КТРУ</strong>
                        </Divider>
                        {yearItem.applications?.byTru?.map((tru, i) => (
                          <Box
                            key={i}
                            sx={{ ml: 2, cursor: "pointer" }}
                            onClick={() =>
                              loadApplications({
                                year: yearItem.year,
                                truCode: tru.item?.code,
                              })
                            }
                          >
                            <Typography>
                              {getValue(tru.item?.name)} (
                              {getValue(tru.item?.code)})
                            </Typography>
                            <Typography variant="body2">
                              Всего: {getValue(tru.stats?.all)}, Побед:{" "}
                              {getValue(tru.stats?.numberOfWinnings)}
                            </Typography>
                          </Box>
                        ))}

                        <Divider>
                          <strong>По методам закупок</strong>
                        </Divider>
                        {yearItem.applications?.byTradeMethod?.map(
                          (method, i) => (
                            <Box
                              key={i}
                              sx={{ ml: 2, cursor: "pointer" }}
                              onClick={() =>
                                loadApplications({
                                  year: yearItem.year,
                                  tradeMethodId: method.item?.code,
                                })
                              }
                            >
                              <Typography>
                                {getValue(method.item?.name)} (
                                {getValue(method.item?.code)})
                              </Typography>
                              <Typography variant="body2">
                                Всего: {getValue(method.stats?.all)}, Побед:{" "}
                                {getValue(method.stats?.numberOfWinnings)}
                              </Typography>
                            </Box>
                          ),
                        )}
                      </Box>

                      {/* список заявок */}
                      {analysisMeta?.applicationsStatus !==
                        ANALYSIS_STATUS.ANALYZED && (
                        <Typography sx={{ mt: 2 }} color="text.secondary">
                          Список заявок недоступен (статус:{" "}
                          {STATUS_LABELS[analysisMeta?.applicationsStatus] ||
                            "неизвестно"}
                          )
                        </Typography>
                      )}
                      {applicationsByYear[yearItem.year]?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Divider>Список заявок</Divider>
                          {applicationsByYear[yearItem.year].map((app, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                border: "1px solid #ddd",
                                borderRadius: "8px",
                                p: 1,
                                my: 1,
                              }}
                            >
                              <Typography>
                                <strong>ID:</strong> {app.applicationId}
                              </Typography>
                              <Typography>
                                <strong>Статус:</strong> {getValue(app.status)}
                              </Typography>
                              <Typography>
                                <strong>Заказчик:</strong>{" "}
                                {app.customer?.name || "Не указано"}
                              </Typography>
                              <Typography>
                                <strong>План. сумма:</strong>{" "}
                                {getValue(app.plannedAmount)}
                              </Typography>
                              <Typography>
                                <strong>Предложенная сумма:</strong>{" "}
                                {getValue(app.amount)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          ) : (
            <Typography color="text.secondary">
              Сводка по заявкам недоступна
              {analysisMeta &&
                ` (статус: ${STATUS_LABELS[analysisMeta.applicationsSummaryStatus] || "неизвестно"})`}
            </Typography>
          )}
        </div>
      )}

      {/* --- контракты --- */}
      {!isAnalysing && !noData && (
        <div className="contracts" style={{ margin: "20px" }}>
          {contractsSummary ? (
            <Card sx={{ marginTop: "20px" }}>
              <CardHeader title="Контракты по годам" />
              <CardContent>
                <Typography sx={{ mb: 2 }}>
                  <strong>Всего контрактов:</strong>{" "}
                  {getValue(contractsSummary.count)}
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  <strong>Общая сумма (без НДС)</strong>{" "}
                  {getValue(contractsSummary.totalAmount)}
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  <strong>Общая сумма (с НДС)</strong>{" "}
                  {getValue(contractsSummary.totalAmountWithNds)}
                </Typography>

                {contractsSummary.byYear?.map((yearItem, idx) => (
                  <Accordion key={idx} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        {yearItem.year} — всего: {getValue(yearItem.count)},
                        Сумма в году (без НДС): {getValue(yearItem.totalAmount)}
                        , Сумма в году (с НДС):{" "}
                        {getValue(contractsSummary.totalAmountWithNds)}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: "grid", gap: 2 }}>
                        <Divider>
                          <strong>по КТРУ</strong>
                        </Divider>
                        {yearItem.contracts?.byTru?.map((tru, i) => (
                          <Box
                            key={i}
                            sx={{ ml: 2, cursor: "pointer" }}
                            onClick={() =>
                              loadContracts({
                                year: yearItem.year,
                                truCode: tru.item?.code,
                              })
                            }
                          >
                            <Typography>
                              {getValue(tru.item?.name)} (
                              {getValue(tru.item?.code)})
                            </Typography>
                            <Typography variant="body2">
                              Всего: {getValue(tru.count)}, Сумма в году (без
                              НДС): {getValue(tru.totalAmount)}, Сумма в году (с
                              НДС): {getValue(tru.totalAmountWithNds)}
                            </Typography>
                          </Box>
                        ))}

                        <Divider>
                          <strong>По методам закупок</strong>
                        </Divider>
                        {yearItem.contracts?.byTradeMethod?.map((method, i) => (
                          <Box
                            key={i}
                            sx={{ ml: 2, cursor: "pointer" }}
                            onClick={() =>
                              loadContracts({
                                year: yearItem.year,
                                tradeMethodId: method.item?.code,
                              })
                            }
                          >
                            <Typography>
                              {getValue(method.item?.name)} (
                              {getValue(method.item?.code)})
                            </Typography>
                            <Typography variant="body2">
                              Всего: {getValue(method.count)}, Сумма в году (без
                              НДС): {getValue(method.totalAmount)}, Сумма в году
                              (с НДС): {getValue(method.totalAmountWithNds)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {analysisMeta?.contractsStatus !==
                        ANALYSIS_STATUS.ANALYZED && (
                        <Typography sx={{ mt: 2 }} color="text.secondary">
                          Список контрактов недоступен (статус:{" "}
                          {STATUS_LABELS[analysisMeta?.contractsStatus] ||
                            "неизвестно"}
                          )
                        </Typography>
                      )}
                      {contractsByYear[yearItem.year]?.data?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Divider>
                            <strong>Список контрактов</strong>
                          </Divider>
                          {contractsByYear[yearItem.year].data.map(
                            (contract, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  border: "1px solid #ddd",
                                  borderRadius: "8px",
                                  p: 1,
                                  my: 1,
                                }}
                              >
                                <p></p>
                                <Typography>
                                  <strong>ID договора:</strong>{" "}
                                  {contract.contractId}
                                </Typography>
                                <Typography>
                                  <strong>Дата подписания:</strong>{" "}
                                  {formatDate(getValue(contract.signDate))}
                                </Typography>
                                <Typography>
                                  <strong>Заказчик:</strong>{" "}
                                  {contract.customer?.name || "Не указано"}
                                </Typography>
                                <Typography>
                                  <strong>Описание:</strong>{" "}
                                  {getValue(contract.announce)}
                                </Typography>
                                <Typography>
                                  <strong>Сумма с НДС:</strong>{" "}
                                  {getValue(contract.totalAmountWithNds)}
                                </Typography>
                                <Typography>
                                  <strong>Сумма без НДС:</strong>{" "}
                                  {getValue(contract.totalAmount)}
                                </Typography>
                              </Box>
                            ),
                          )}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          ) : (
            <Typography color="text.secondary">
              Сводка по контрактам недоступна
              {analysisMeta &&
                ` (статус: ${STATUS_LABELS[analysisMeta.contractsSummaryStatus] || "неизвестно"})`}
            </Typography>
          )}
        </div>
      )}

      {/* --- налоги --- */}
      {!isAnalysing && !noData && (
        <div className="taxes" style={{ margin: "20px" }}>
          {taxes?.yearlySum?.length ? (
            <Card sx={{ marginTop: "20px" }}>
              <CardHeader title="Налоги" />
              {taxes.yearlySum.map((year, idx) => (
                <Card key={idx} sx={{ m: 2 }}>
                  <Typography sx={{ m: 1 }}>
                    <strong>Год: </strong> {getValue(year.key)}
                  </Typography>
                  <Typography sx={{ m: 1 }}>
                    <strong>Налог: </strong> {getValue(year.value)}
                  </Typography>
                </Card>
              ))}
            </Card>
          ) : (
            <Typography color="text.secondary">
              {/* taxesStatus: 4 (HaventAnalyzed) — это норма, налоги почти никогда не приходят */}
              Нет данных по налогам
              {analysisMeta &&
                analysisMeta.taxesStatus !== ANALYSIS_STATUS.HAVENT_ANALYZED &&
                ` (статус: ${STATUS_LABELS[analysisMeta.taxesStatus] || "неизвестно"})`}
            </Typography>
          )}
        </div>
      )}
    </>
  );
};

export default BinActiveUserPage;
